import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaChartPie } from 'react-icons/fa';
import axios from 'axios';
import { format } from 'date-fns';
import { useAuth } from '../security/security/AuthContext';

const ExpenseManagement = () => {
  const [expenses, setExpenses] = useState([]);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();
  const [approvedWithdrawals, setApprovedWithdrawals] = useState(0);

  const [formData, setFormData] = useState({
    category: '',
    description: '',
    amount: ''
  });

  const expenseCategories = [
    'Development',
    'Maintenance', 
    'Marketing (Ad )',
    'Shares',
    'Infrastructure',
    'Office Expenses',
    'Customer Support',
    'Training',
    'Software Licenses',
    'Miscellaneous',
  ];

  useEffect(() => {
    fetchExpenses();
    fetchFinancialSummary();
    fetchApprovedWithdrawals();
  }, []);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://api.novasoftwaves.com/api/admin/expenses', {
        headers: { Authorization: token }
      });
      setExpenses(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovedWithdrawals = async () => {
    try {
      const response = await axios.get('https://api.novasoftwaves.com/api/admin/withdrawals?status=APPROVED', {
        headers: { Authorization: token }
      });
      const totalWithdrawals = response.data.reduce((sum, withdrawal) => sum + withdrawal.amountToReceive, 0);
      setApprovedWithdrawals(totalWithdrawals);
    } catch (error) {
      console.error('Error fetching approved withdrawals:', error);
    }
  };


  const fetchFinancialSummary = async () => {
    try {
      const response = await axios.get('https://api.novasoftwaves.com/api/admin/expenses/summary', {
        headers: { Authorization: token }
      });
      setFinancialSummary(response.data);
    } catch (error) {
      console.error('Error fetching financial summary:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedExpense) {
        await axios.put(
          `https://api.novasoftwaves.com/api/admin/expenses/${selectedExpense.id}`,
          formData,
          { headers: { Authorization: token } }
        );
      } else {
        await axios.post(
          'https://api.novasoftwaves.com/api/admin/expenses',
          formData,
          { headers: { Authorization: token } }
        );
      }
      setIsModalOpen(false);
      setSelectedExpense(null);
      setFormData({ category: '', description: '', amount: '' });
      fetchExpenses();
      fetchFinancialSummary();
    } catch (error) {
      console.error('Error saving expense:', error);
    }
  };

  const handleEdit = (expense) => {
    setSelectedExpense(expense);
    setFormData({
      category: expense.category,
      description: expense.description,
      amount: expense.amount.toString()
    });
    setIsModalOpen(true);
  };

  // Calculate actual available balance by subtracting approved withdrawals
  const calculateAvailableBalance = () => {
    if (!financialSummary) return 0;
    return financialSummary.totalRevenue - financialSummary.totalExpenses - approvedWithdrawals;
  };

  return (
    <div className="adminpage-content">
      <div className="adminpage-stats-grid">
        <div className="adminpage-stat-card adminpage-gradient-green">
          <div className="adminpage-stat-content">
            <div className="adminpage-stat-info">
              <h6>Total Revenue</h6>
              <h2>KSH {financialSummary?.totalRevenue?.toLocaleString() || '0'}</h2>
            </div>
          </div>
        </div>

        <div className="adminpage-stat-card adminpage-gradient-red">
          <div className="adminpage-stat-content">
            <div className="adminpage-stat-info">
              <h6>Total Expenses</h6>
              <h2>KSH {financialSummary?.totalExpenses?.toLocaleString() || '0'}</h2>
            </div>
          </div>
        </div>

        <div className="adminpage-stat-card adminpage-gradient-orange">
          <div className="adminpage-stat-content">
            <div className="adminpage-stat-info">
              <h6>Total Withdrawals</h6>
              <h2>KSH {approvedWithdrawals.toLocaleString()}</h2>
            </div>
          </div>
        </div>

        <div className="adminpage-stat-card adminpage-gradient-blue">
          <div className="adminpage-stat-content">
            <div className="adminpage-stat-info">
              <h6>Available Balance</h6>
              <h2>KSH {calculateAvailableBalance().toLocaleString()}</h2>
            </div>
          </div>
        </div>
      </div>

      <div className="adminpage-content-grid">
        <div className="adminpage-card">
          <div className="adminpage-card-header">
            <h5>Expense Management</h5>
            <button
              className="adminpage-btn-primary"
              onClick={() => setIsModalOpen(true)}
            >
              <FaPlus /> Add Expense
            </button>
          </div>
          
          <div className="adminpage-card-body">
            <table className="adminpage-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((expense) => (
                  <tr key={expense.id}>
                    <td>{expense.category}</td>
                    <td>{expense.description}</td>
                    <td>KSH {expense.amount.toLocaleString()}</td>
                    <td>{format(new Date(expense.createdAt), 'PPp')}</td>
                    <td>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {financialSummary?.expensesByCategory && (
          <div className="adminpage-card">
            <div className="adminpage-card-header">
              <h5>Expense Breakdown</h5>
            </div>
            <div className="adminpage-card-body">
              <table className="adminpage-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Total Amount</th>
                    <th>Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  {financialSummary.expensesByCategory.map((category) => (
                    <tr key={category.category}>
                      <td>{category.category}</td>
                      <td>KSH {category.totalAmount.toLocaleString()}</td>
                      <td>{category.percentageOfExpenses.toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="adminpage-modal-overlay">
          <div className="adminpage-modal">
            <div className="adminpage-modal-header">
              <h5>{selectedExpense ? 'Edit Expense' : 'Add New Expense'}</h5>
              <button
                className="adminpage-modal-close"
                onClick={() => {
                setIsModalOpen(false);
                setSelectedExpense(null);
                setFormData({ category: '', description: '', amount: '' });
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="adminpage-modal-body">
                <div className="adminpage-form-group">
                  <label>Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  >
                    <option value="">Select Category</option>
                    {expenseCategories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="adminpage-form-group">
                  <label>Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter expense description"
                    required
                  />
                </div>
                <div className="adminpage-form-group">
                  <label>Amount (KSH)</label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="Enter amount"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
              </div>
              <div className="adminpage-modal-footer">
                <button
                  type="button"
                  className="adminpage-btn-light"
                  onClick={() => {
                    setIsModalOpen(false);
                    setSelectedExpense(null);
                    setFormData({ category: '', description: '', amount: '' });
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="adminpage-btn-primary">
                  {selectedExpense ? 'Update Expense' : 'Add Expense'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseManagement;
